/**
 * Rule of 4 and 2 quiz. Outs × 4 on flop, × 2 on turn.
 */

import { Brain } from "@/shared/components/icons";
import { QuizLayout } from "@/features/quiz/components/QuizLayout";
import { QuizOption } from "@/features/quiz/components/QuizOption";
import { RevealPanel } from "@/features/quiz/components/RevealPanel";
import { Stat } from "@/features/quiz/components/Stat";
import { useQuizSession } from "@/features/quiz/lib/useQuizSession";
import { type RuleOf42Question, generateQuestion } from "@/features/quiz/lib/ruleOf42Quiz";

export function QuizRuleOf42() {
  const { question, revealedAnswer, isCorrect, correctAnswer, stats, submit, next } =
    useQuizSession<RuleOf42Question, number>({
      quizType: "rule_of_4_2",
      generate: generateQuestion,
      getCorrectAnswer: (q) => q.answer,
      getQuestionPayload: (q) => ({ outs: q.outs, street: q.street }),
    });

  const multiplier = question.street === "flop" ? 4 : 2;
  const cardsLeft = question.street === "flop" ? "2 cards to come" : "1 card to come";

  return (
    <QuizLayout
      Icon={Brain}
      title="Rule of 4 and 2"
      subtitle="Outs → approximate equity"
      description="Tests the standard table-time approximation that converts outs to equity: multiply by 4 on the flop, by 2 on the turn. Once you've counted outs, this is how you decide in seconds whether you're getting the price to continue."
      stats={stats}
    >
      <div className="flex flex-col items-center gap-4 py-4">
        <div className="flex items-baseline gap-8">
          <Stat label="OUTS" value={String(question.outs)} accent />
          <Stat label="STREET" value={question.street.toUpperCase()} />
        </div>
        <div className="text-xs text-text-muted">{cardsLeft}</div>
      </div>

      <p className="text-center text-text text-base">Approximate equity by the river?</p>

      <div className="grid grid-cols-2 gap-3">
        {question.options.map((eq) => (
          <QuizOption
            key={eq}
            value={eq}
            revealedAnswer={revealedAnswer}
            correctAnswer={correctAnswer}
            onSelect={submit}
          >
            <span className="text-lg">{eq}%</span>
          </QuizOption>
        ))}
      </div>

      {revealedAnswer !== null && (
        <RevealPanel
          isCorrect={isCorrect}
          answerLine={
            <>
              Answer: <strong>{correctAnswer}%</strong>
            </>
          }
          rationale={
            <>
              On the <strong>{question.street}</strong> ({cardsLeft}), multiply outs by <strong>{multiplier}</strong>: {question.outs} × {multiplier} = {correctAnswer}%. The rule slightly overestimates at high out counts (15+) but it's the right shortcut for table use.
            </>
          }
          onNext={next}
        />
      )}
    </QuizLayout>
  );
}

